import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Form,
  Input,
  Popconfirm,
  Tooltip,
  message,
} from "antd";
import {
  SearchOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  fetchUserQueries,
  deleteUserQuery,
} from "../../../api/userQueryApi"; // adjust path if needed
import { toastError, toastSuccess } from "../../../utils/toast"; // adjust path if needed

export default function UserQueriesPage() {
  const [loading, setLoading] = useState(false);
  const [queries, setQueries] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchForm] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchUserQueries();
      setQueries(data);
      setFiltered(data);
    } catch (e) {
      toastError("Failed to load user queries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onSearch = (values) => {
    const q = (values.search || "").trim().toLowerCase();
    if (!q) {
      setFiltered(queries);
      return;
    }
    const list = queries.filter(
      (row) =>
        (row.name || "").toLowerCase().includes(q) ||
        (row.email || "").toLowerCase().includes(q)
    );
    setFiltered(list);
  };

  const onReset = () => {
    searchForm.resetFields();
    setFiltered(queries);
  };

  const handleDelete = async (id) => {
    try {
      await deleteUserQuery(id);
      toastSuccess("Query deleted");
      load();
    } catch (e) {
      toastError("Failed to delete query");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
      render: (t) => <strong>{t}</strong>,
    },
    {
      title: "Service Name",
      dataIndex: "service_name",
      sorter: (a, b) => (a.service_name || "").localeCompare(b.service_name || ""),
      render: (t) => <strong>{t}</strong>,
    },
    {
      title: "Email",
      dataIndex: "email",
      sorter: (a, b) => (a.email || "").localeCompare(b.email || ""),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      sorter: (a, b) => (a.phone || "").localeCompare(b.phone || ""),
    },

    {
      title: "Address",
      dataIndex: "address",
      sorter: (a, b) => (a.address || "").localeCompare(b.address || ""),
    },    {
      title: "Message",
      dataIndex: "message",
      ellipsis: true,
      render: (t) => (t ? <span title={t}>{t}</span> : "-"),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      width: 160,
      sorter: (a, b) =>
        new Date(a.created_at || 0) - new Date(b.created_at || 0),
      render: (d) => (d ? dayjs(d).format("YYYY-MM-DD") : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 110,
      render: (_, record) => (
        <Space>
          <Popconfirm
            title="Delete this query?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record.id)}
          >
            <Tooltip title="Delete">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card bordered={false} style={{ borderRadius: 12 }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <h2 style={{ margin: 0 }}>User Queries</h2>

        {/* Inline Search: matches name OR email */}
        <Form
          form={searchForm}
          layout="inline"
          onFinish={onSearch}
          style={{ display: "flex", gap: 8 }}
        >
          <Form.Item name="search">
            <Input
              allowClear
              placeholder="Search by name or email"
              prefix={<SearchOutlined />}
              style={{ minWidth: 260 }}
              onPressEnter={() => searchForm.submit()}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                Search
              </Button>
              <Button onClick={onReset}>Reset</Button>
            </Space>
          </Form.Item>
        </Form>
      </div>

      {/* Table */}
      <Table
        rowKey="id"
        columns={columns}
        dataSource={filtered}
        loading={loading}
        pagination={{ pageSize: 10, showSizeChanger: true }}
        expandable={{
          expandedRowRender: (record) => (
            <div style={{ paddingRight: 24, whiteSpace: "pre-wrap" }}>
              <strong>Message:</strong>
              <br />
              {record.message || "-"}
            </div>
          ),
        }}
      />
    </Card>
  );
}
